import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LanguageService } from './language.service';

/**
 * Mirrors the Flutter BackendMessageTranslator.
 *
 * Loads `assets/i18n/backend_messages.xml` once at startup and exposes a
 * `translate(message)` method that returns the English equivalent when the
 * app is set to English, or the original message otherwise.
 *
 * Usage (TypeScript):
 *   this.backendTranslator.translate(errorFromBackend)
 *
 * Usage (template via pipe):
 *   {{ errorMessage | translateBackendMsg }}
 */
@Injectable({ providedIn: 'root' })
export class BackendMessageTranslatorService {
  private readonly assetPath = 'assets/i18n/backend_messages.xml';

  /** source (ES) → english translation, exact match */
  private exactEnglishMessages = new Map<string, string>();
  /** normalized source → english translation */
  private normalizedEnglishMessages = new Map<string, string>();

  private isLoaded = false;

  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
  ) {}

  /** Called from APP_INITIALIZER – loads and parses the XML once. */
  load(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    return this.http
      .get(this.assetPath, { responseType: 'text' })
      .toPromise()
      .then((xml: string) => {
        this.parseXml(xml);
      })
      .catch((error) => {
        console.warn('[BackendMessageTranslator] Could not load asset:', error);
      })
      .finally(() => {
        this.isLoaded = true;
      });
  }

  /**
   * Returns the translated message if the current language is English,
   * otherwise returns the original message unchanged.
   */
  translate(message: string | null | undefined): string {
    if (!message) {
      return message ?? '';
    }

    const isEnglish = this.languageService.currentLang === 'en';
    if (!isEnglish || !this.isLoaded) {
      return message;
    }

    const trimmed = message.trim();
    if (!trimmed) {
      return message;
    }

    // 1. Exact match
    const exactMatch = this.exactEnglishMessages.get(trimmed);
    if (exactMatch) {
      return exactMatch;
    }

    // 2. Normalized match (case-insensitive, collapsed whitespace)
    const normalizedMatch = this.normalizedEnglishMessages.get(this.normalize(trimmed));
    if (normalizedMatch) {
      return normalizedMatch;
    }

    // 3. Line-by-line translation
    const byLine = this.translateByLine(trimmed);
    if (byLine !== null) {
      return byLine;
    }

    // 4. Fragment replacement
    const byFragment = this.translateByFragment(trimmed);
    if (byFragment !== null) {
      return byFragment;
    }

    // Fallback: return original
    return message;
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private parseXml(xmlString: string): void {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'application/xml');

    const messages = doc.getElementsByTagName('message');

    this.exactEnglishMessages.clear();
    this.normalizedEnglishMessages.clear();

    for (let i = 0; i < messages.length; i++) {
      const node = messages[i];
      const source = node.getAttribute('source')?.trim();
      const english = node.getAttribute('en')?.trim();

      if (!source || !english) {
        continue;
      }

      this.exactEnglishMessages.set(source, english);
      this.normalizedEnglishMessages.set(this.normalize(source), english);
    }
  }

  private translateByLine(message: string): string | null {
    const lines = message.split('\n');
    let changed = false;

    const translatedLines = lines.map((line) => {
      const translated = this.normalizedEnglishMessages.get(this.normalize(line));
      if (translated !== undefined) {
        changed = true;
        return translated;
      }
      return line;
    });

    return changed ? translatedLines.join('\n') : null;
  }

  private translateByFragment(message: string): string | null {
    // Sort by key length descending so longer phrases are replaced first
    const entries = Array.from(this.exactEnglishMessages.entries()).sort(
      (a, b) => b[0].length - a[0].length,
    );

    let translated = message;
    let changed = false;

    for (const [source, english] of entries) {
      if (translated.includes(source)) {
        translated = translated.split(source).join(english);
        changed = true;
      }
    }

    return changed ? translated : null;
  }

  private normalize(value: string): string {
    return value.toLowerCase().replace(/\s+/g, ' ').trim();
  }
}
